// In-app error reports, browser side. Uncaught errors, unhandled promise
// rejections, React render crashes (through ErrorBoundary) and 5xx / network
// failures of our own API are posted to /api/public/client-errors, where the
// backend groups them for the operator page /admin/errors (error_reports.py).
//
// Rules: never throw, never block the UI, never report its own request (it uses
// fetch, not axios), each distinct error once per page load and at most
// MAX_PER_PAGE reports per page load. Query strings are dropped here and the
// backend scrubs again.
//
// A request that dies without any response ("network" failure) is only reported
// while the page is settled: visible, online, not unloading, and visible+online
// for NETWORK_SETTLE_MS. Background tabs, sleeping laptops and page reloads
// otherwise produce a steady stream of reports that are not bugs (the 30 s
// notification poll firing as a laptop wakes, before Wi-Fi is back).

export const MAX_PER_PAGE = 20;

const LIMITS = {
  message: 1000,
  stack: 12000,
  componentStack: 4000,
  url: 1000,
};

let endpoint = null;
let release = '';
let sent = 0;
const seen = new Set();

export const NETWORK_SETTLE_MS = 15000;
let settledSince = 0;
let unloading = false;

function pageIsSettled() {
  try {
    if (unloading) return false;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
    return Date.now() - settledSince >= NETWORK_SETTLE_MS;
  } catch (e) {
    return true;
  }
}

function clip(value, max) {
  if (value === undefined || value === null || value === '') return undefined;
  const text = String(value);
  return text.length > max ? text.slice(0, max) : text;
}

function stripQuery(url) {
  if (!url) return undefined;
  return String(url).split(/[?#]/)[0];
}

function currentUrl() {
  try {
    return window.location.href;
  } catch (e) {
    return undefined;
  }
}

function storedToken() {
  try {
    return window.localStorage.getItem('token');
  } catch (e) {
    return null;
  }
}

export function buildPayload(input = {}) {
  const error = input.error;
  const fromError = error && error.message ? error.message : '';
  const message = input.message || fromError || 'Unknown error';
  const stack = input.stack || (error && error.stack);
  return {
    kind: input.kind || 'error',
    message: clip(message, LIMITS.message),
    stack: clip(stack, LIMITS.stack),
    component_stack: clip(input.componentStack, LIMITS.componentStack),
    url: clip(stripQuery(input.url || currentUrl()), LIMITS.url),
    release: release || undefined,
    api_method: input.apiMethod ? String(input.apiMethod).toUpperCase().slice(0, 10) : undefined,
    api_path: clip(stripQuery(input.apiPath), LIMITS.url),
    api_status: typeof input.apiStatus === 'number' ? input.apiStatus : undefined,
  };
}

export function reportError(input = {}) {
  try {
    if (!endpoint || sent >= MAX_PER_PAGE) return false;
    const payload = buildPayload(input);
    const key = [
      payload.kind,
      payload.message,
      payload.api_method,
      payload.api_path,
      payload.api_status,
    ].join('|');
    if (seen.has(key)) return false;
    seen.add(key);
    sent += 1;
    const headers = { 'Content-Type': 'application/json' };
    const token = storedToken();
    if (token) headers.Authorization = 'Bearer ' + token;
    const request = fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
    });
    if (request && typeof request.catch === 'function') request.catch(() => {});
    return true;
  } catch (e) {
    return false;
  }
}

function isOurApi(url, apiBase) {
  const text = String(url || '');
  return text.startsWith(apiBase) || text.startsWith('/api');
}

function reportAxiosFailure(error, apiBase) {
  const config = (error && error.config) || {};
  if (!isOurApi(config.url, apiBase)) return;
  if (error && (error.code === 'ERR_CANCELED' || error.name === 'CanceledError')) return;
  const status = error && error.response ? error.response.status : undefined;
  if (status !== undefined && status < 500) return;
  // No response at all: report only when the page is settled (see the header).
  if (status === undefined && !pageIsSettled()) return;
  reportError({
    kind: status ? 'api' : 'network',
    message: error && error.message,
    apiMethod: config.method,
    apiPath: config.url,
    apiStatus: status,
  });
}

export function installErrorReporter(options = {}) {
  const { apiBase, axiosInstance, allowInTests = false } = options;
  if (endpoint || !apiBase || typeof window === 'undefined') return false;
  if (process.env.NODE_ENV === 'test' && !allowInTests) return false;
  endpoint = apiBase + '/public/client-errors';
  release = options.release || '';

  window.addEventListener('error', (event) => {
    // Failed <img>/<script> loads have neither an error object nor a message.
    if (!event || (!event.error && !event.message)) return;
    reportError({ kind: 'error', error: event.error, message: event.message });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event ? event.reason : undefined;
    // API failures are reported by the axios interceptor below.
    if (reason && reason.isAxiosError) return;
    if (reason instanceof Error) {
      reportError({ kind: 'unhandledrejection', error: reason });
    } else {
      reportError({ kind: 'unhandledrejection', message: String(reason) });
    }
  });

  const unsettle = () => {
    settledSince = Date.now();
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') unsettle();
  });
  window.addEventListener('online', unsettle);
  window.addEventListener('pageshow', () => {
    unloading = false;
    unsettle();
  });
  window.addEventListener('pagehide', () => {
    unloading = true;
  });
  window.addEventListener('beforeunload', () => {
    unloading = true;
  });

  if (axiosInstance && axiosInstance.interceptors) {
    axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        try {
          reportAxiosFailure(error, apiBase);
        } catch (e) {
          // Reporting must never change how the caller sees the error.
        }
        return Promise.reject(error);
      }
    );
  }
  return true;
}

export function resetErrorReporterForTests() {
  endpoint = null;
  release = '';
  sent = 0;
  seen.clear();
  settledSince = 0;
  unloading = false;
}
